import { OnChangeFn, PaginationState } from "@tanstack/react-table";
import { FC, useCallback, useEffect, useMemo } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { TableOptionsContext, TableWrapperContext } from "@ryde/controls";
import { useReportsContext } from "@ryde/redux";
import {
  apiDataFetchingAtom,
  defaultReportPaginationState,
  IRydeTableOptions,
  ITableDto,
  ReportRenderTypeEnum,
  reportSearchMatcher,
  reportsTableColumnsOrderStateAF,
  reportsTableColumnsSortingAF,
  reportsTableColumnsVisibilityAF,
  reportsTableGroupingAF,
  reportTableItemPaginationAF,
  reportTableRowsAF,
  rideFilterSearchAtom,
  shouldDebugReactTable,
  useRydeTable,
} from "@ryde/shared";

import {
  GridRowLimitExceededPanel,
  NeedGroupingReportPanel,
  ReportDownload,
  ReportTableHead,
} from "../parts";
import { ReportGridInner } from "./report-grid-inner";
import { useTableDefsReports } from "./use-table-def.reports";

export const ReportGrid: FC = () => {
  const apiDataLoading = useRecoilValue(apiDataFetchingAtom);

  const { reportRenderType, reportId, reportDto } = useReportsContext();
  const [tableItemPagination, setTableItemPagination] = useRecoilState(
    reportTableItemPaginationAF(reportId),
  );

  useEffect(() => {
    if (
      tableItemPagination?.pageSize !== defaultReportPaginationState.pageSize
    ) {
      setTableItemPagination(defaultReportPaginationState);
    }
  }, [setTableItemPagination, tableItemPagination]);

  const getRowIdCB = useCallback((originalRow: ITableDto, _index: number) => {
    return String(originalRow?.id);
  }, []);
  const { tableDefsReports } = useTableDefsReports();

  const [columnSorting, setColumnSorting] = useRecoilState(
    reportsTableColumnsSortingAF(reportId),
  );
  const [columnGrouping, setColumnGrouping] = useRecoilState(
    reportsTableGroupingAF(reportId),
  );

  const [columnOrder, setColumnOrder] = useRecoilState(
    reportsTableColumnsOrderStateAF(reportId),
  );

  const [columnVisibility, setColumnVisibility] = useRecoilState(
    reportsTableColumnsVisibilityAF(reportId),
  );

  const setReportTableRows = useSetRecoilState(reportTableRowsAF(reportId));

  const searchValue = useRecoilValue(rideFilterSearchAtom(reportId));

  const tableData = useMemo(() => {
    const reportRows = reportDto?.rows || [];

    if (!searchValue) {
      return reportRows;
    }

    const visibleFieldNames: string[] = Object.entries(columnVisibility)
      .filter(([, value]) => value)
      .map(([key]) => key);

    return reportRows.filter(row => {
      return reportSearchMatcher({ row, searchValue, visibleFieldNames });
    });
  }, [columnVisibility, reportDto?.rows, searchValue]);

  useEffect(() => {
    setReportTableRows(tableData);
  }, [setReportTableRows, tableData]);

  const tableOptions: IRydeTableOptions<ITableDto> = {
    data: tableData,
    columns: tableDefsReports,
    state: {
      pagination: tableItemPagination ?? defaultReportPaginationState,
      columnVisibility,
      columnOrder,
      sorting: columnSorting,
    },
    onPaginationChange: setTableItemPagination as OnChangeFn<PaginationState>,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onSortingChange: setColumnSorting,
    getRowCanExpand: () => true,
    debugTable: shouldDebugReactTable(),
    autoResetPageIndex: true,
    autoResetExpanded: false,
    getRowId: getRowIdCB,
  };

  const table = useRydeTable(tableOptions);

  const needGroupingSelect = useMemo(() => {
    return !apiDataLoading && Boolean(reportDto?.groupingParamsExpected);
  }, [apiDataLoading, reportDto?.groupingParamsExpected]);

  const isRawReport = reportRenderType === ReportRenderTypeEnum.RawData;
  // Result exceeded the template's grid row limit — rows were withheld by the backend,
  // so offer the same download-only experience as RawData reports (full CSV from the server).
  // Gate purely on the dto flag (like isRawReport and the report-table-head toolbar) — NOT on
  // apiDataLoading: fetchReportCSV shares apiDataFetchingAtom, so a loading check would unmount
  // this panel + ReportDownload for the whole server-side download and flash an empty grid.
  const isGridRowLimitExceeded = Boolean(reportDto?.gridRowLimitExceeded);
  const isDownloadOnly = isRawReport || isGridRowLimitExceeded;

  return (
    <TableWrapperContext.Provider
      value={{
        columnDefs: tableDefsReports,
        columnGrouping,
        columnOrder,
        columnSorting,
        columnVisibility,
        setColumnGrouping,
        setColumnOrder,
        setColumnSorting,
        setColumnVisibility,
        // Reports never render the rides views or the linked id-edit column.
        isGridSingleSelectView: false,
        isRidesEditOrAddRoute: false,
        idColumnEditHrefSearch: "",
      }}
    >
      <TableOptionsContext.Provider
        value={{
          table,
          tableOptions,
        }}
      >
        <ReportTableHead />
        {isGridRowLimitExceeded && <GridRowLimitExceededPanel />}
        {isDownloadOnly && <ReportDownload />}
        {!isDownloadOnly && (
          <>
            {!needGroupingSelect && <ReportGridInner />}
            {needGroupingSelect && <NeedGroupingReportPanel />}
          </>
        )}
      </TableOptionsContext.Provider>
    </TableWrapperContext.Provider>
  );
};
